import * as React from 'react';
import Divider from 'components/Divider';
import Heading from 'components/Heading';
import Icon from 'components/Icon';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import { getReadableDeviceType, DeviceType } from 'auth/components/ConfirmDevice';
import { MouseEventHandler } from 'types/typescript/eventHandlers';

export type Props = {
  uid: string;
  deviceType: DeviceType;
  needsPassword: boolean;
  onGoBack?: MouseEventHandler;
  needsConfirmationErrorCode?: string;
};

export default function ConfirmDeviceHeader(props: Props) {
  const getHeading = () => {
    const { needsPassword, needsConfirmationErrorCode, deviceType } = props;
    const defaultText = `Confirm your ${getReadableDeviceType(deviceType)}`;
    if (needsPassword) {
      return defaultText;
    } else if (needsConfirmationErrorCode) {
      const reconfirmText = 'It’s been a while since you logged in';
      const confirmText = 'This looks like a new device';
      const codeToHeadingMap: Record<string, string> = {
        unconfirmed_contactable_email: 'Check your email',
        unconfirmed_contactable_phone: 'Check your text messages',
        session_login_reconfirm_email: reconfirmText,
        session_login_reconfirm_phone: reconfirmText,
        session_login_confirm_email: confirmText,
        session_login_confirm_phone: confirmText,
      };
      return codeToHeadingMap[needsConfirmationErrorCode] ?? defaultText;
    }
    return defaultText;
  };

  const getReason = () => {
    const { needsPassword, needsConfirmationErrorCode } = props;
    const confirmText = 'To confirm your account';
    const verifyText = 'To verify it’s you';

    if (needsPassword) {
      return 'Your account doesn’t have a password yet. To set it up';
    } else if (needsConfirmationErrorCode) {
      const codeToReasonMap: Record<string, string> = {
        unconfirmed_contactable_email: confirmText,
        unconfirmed_contactable_phone: confirmText,
        session_login_reconfirm_email: verifyText,
        session_login_reconfirm_phone: verifyText,
        session_login_confirm_email: verifyText,
        session_login_confirm_phone: verifyText,
      };
      return codeToReasonMap[needsConfirmationErrorCode] ?? confirmText;
    }
    return confirmText;
  };

  const { uid, onGoBack, deviceType } = props;
  return (
    <>
      {onGoBack && (
        <Row size="small">
          <Text align="left">
            <Link onClick={onGoBack} eventKey={`log_in.confirm_${deviceType}_page.go_back.click`}>
              <Icon name="arrow-left" />
              {` Back`}
            </Link>
          </Text>
        </Row>
      )}
      <Row size="small">
        <Heading data-test="confirm-device-heading">{getHeading()}</Heading>
        <Text color="graphite">
          {`${getReason()}, enter the 4-digit code that was just sent to `}
          <Text weight="semibold">{uid}</Text>
        </Text>
      </Row>
      <Divider size="medium" />
    </>
  );
}
